import React, { useEffect, useState } from "react";
import axiosInstance from "../../../../services/AxiosInstance";
import CustomSelect, { CustomSelectParqueaderos } from "./Select";
import CustomClearIndicator from "./MultiSelect";

export const FIELD_LAYOUTS = Object.freeze({
  HORIZONTAL: "horizontal",
  VERTICAL: "vertical",
});

const DEFAULT_VIVIENDA_LABEL = "Inmueble";
const DEFAULT_VIVIENDA_PLACEHOLDER = "Selecciona un inmueble";
const DEFAULT_MULTI_VIVIENDA_PLACEHOLDER = "Selecciona uno o más inmuebles";
const DEFAULT_PARQUEADERO_LABEL = "Parqueadero";
const DEFAULT_PARQUEADERO_PLACEHOLDER = "Selecciona un parqueadero";

const VIVIENDAS_ENDPOINT = "/api/v3/usuarios/citofonos/dropdown/";
const PARQUEADEROS_ENDPOINT =
  "/api/v3/visitantes/ingreso-vehicular/dropdown_parqueaderos/";

const normalizeDropdownData = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
};

const useDropdownOptions = (endpoint) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    let isMounted = true;

    if (!endpoint) {
      setOptions([]);
      return () => {
        isMounted = false;
      };
    }

    const fetchOptions = async () => {
      try {
        const response = await axiosInstance.get(endpoint);
        const data = normalizeDropdownData(response?.data);
        if (isMounted) {
          setOptions(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error(`Error fetching options from ${endpoint}:`, error);
          setOptions([]);
        }
      }
    };

    fetchOptions();

    return () => {
      isMounted = false;
    };
  }, [endpoint]);

  return options;
};

export const FieldBasic = ({
  children,
  errors,
  label,
  htmlFor,
  required,
  helperText,
  layout = FIELD_LAYOUTS.HORIZONTAL,
  className,
  labelColClass = "col-lg-4",
  fieldColClass = "col-lg-7",
  contentClassName,
}) => {
  const containerClass =
    className ||
    (layout === FIELD_LAYOUTS.HORIZONTAL
      ? `form-group row ${label ? "mb-3" : ""}`
      : `form-group ${label ? "mb-3" : ""}`);

  const contentClass =
    contentClassName ||
    (layout === FIELD_LAYOUTS.HORIZONTAL
      ? label
        ? fieldColClass
        : "col-12"
      : undefined);

  const helperNode = helperText ? (
    <div className="form-text text-muted">{helperText}</div>
  ) : null;

  const errorNode = errors ? (
    <div
      id={htmlFor ? `val-${htmlFor}-error` : undefined}
      className="invalid-feedback animated fadeInUp"
      style={{ display: "block" }}
    >
      {errors}
    </div>
  ) : null;

  if (layout === FIELD_LAYOUTS.VERTICAL) {
    return (
      <div className={containerClass}>
        {label && (
          <label className="form-label" htmlFor={htmlFor}>
            {label}
            {required && <span className="text-danger"> *</span>}
          </label>
        )}
        <div className={contentClass}>
          {children}
          {helperNode}
          {errorNode}
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {label ? (
        <>
          <label
            className={`${labelColClass} col-form-label`}
            htmlFor={htmlFor}
          >
            {label}
            {required && <span className="text-danger"> *</span>}
          </label>
          <div className={contentClass}>
            {children}
            {helperNode}
            {errorNode}
          </div>
        </>
      ) : (
        <div className={contentClass}>
          {children}
          {helperNode}
          {errorNode}
        </div>
      )}
    </div>
  );
};

FieldBasic.LAYOUTS = FIELD_LAYOUTS;
FieldBasic.layouts = FIELD_LAYOUTS;

export const SelectVivienda = ({
  errors,
  label = DEFAULT_VIVIENDA_LABEL,
  placeholder = DEFAULT_VIVIENDA_PLACEHOLDER,
  endpoint = VIVIENDAS_ENDPOINT,
  layout = FIELD_LAYOUTS.HORIZONTAL,
  helperText,
  required = true,
  showLabel = false,
  fieldClassName,
  contentClassName,
  selectClassName,
  options: customOptions,
  ...selectProps
}) => {
  const resolvedOptions = customOptions ?? useDropdownOptions(endpoint);
  const resolvedLabel = showLabel ? label : null;
  const resolvedId = selectProps.id ?? selectProps.name ?? undefined;
  const selectComponentProps = {
    ...selectProps,
    id: resolvedId,
    name: selectProps.name ?? "vivienda",
  };

  return (
    <FieldBasic
      label={resolvedLabel}
      htmlFor={resolvedId}
      required={required && Boolean(resolvedLabel)}
      errors={errors}
      helperText={helperText}
      layout={layout}
      className={fieldClassName}
      contentClassName={contentClassName}
    >
      <CustomSelect
        options={resolvedOptions}
        placeholder={placeholder}
        className={selectClassName}
        {...selectComponentProps}
      />
    </FieldBasic>
  );
};

export const MultiSelectSelectVivienda = ({
  errors,
  label = DEFAULT_VIVIENDA_LABEL,
  placeholder = DEFAULT_MULTI_VIVIENDA_PLACEHOLDER,
  endpoint = VIVIENDAS_ENDPOINT,
  layout = FIELD_LAYOUTS.HORIZONTAL,
  helperText,
  required = false,
  showLabel = true,
  fieldClassName,
  contentClassName,
  selectClassName,
  defaultsValues,
  isMulti = true,
  options: customOptions,
  ...selectProps
}) => {
  const resolvedOptions = customOptions ?? useDropdownOptions(endpoint);
  const resolvedLabel = showLabel ? label : null;
  const resolvedId = selectProps.id ?? selectProps.name ?? undefined;
  const selectComponentProps = {
    ...selectProps,
    id: resolvedId,
    name: selectProps.name ?? "viviendas",
  };

  return (
    <FieldBasic
      label={resolvedLabel}
      htmlFor={resolvedId}
      required={required && Boolean(resolvedLabel)}
      errors={errors}
      helperText={helperText}
      layout={layout}
      className={fieldClassName}
      contentClassName={contentClassName}
    >
      <CustomClearIndicator
        options={resolvedOptions}
        defaultsValues={defaultsValues}
        isMulti={isMulti}
        className={selectClassName}
        placeholder={placeholder}
        {...selectComponentProps}
      />
    </FieldBasic>
  );
};

export const SelectParqueaderos = ({
  errors,
  label = DEFAULT_PARQUEADERO_LABEL,
  placeholder = DEFAULT_PARQUEADERO_PLACEHOLDER,
  endpoint = PARQUEADEROS_ENDPOINT,
  layout = FIELD_LAYOUTS.HORIZONTAL,
  helperText,
  required = true,
  showLabel = false,
  fieldClassName,
  contentClassName,
  selectClassName,
  options: customOptions,
  ...selectProps
}) => {
  const resolvedOptions = customOptions ?? useDropdownOptions(endpoint);
  const resolvedLabel = showLabel ? label : null;
  const resolvedId = selectProps.id ?? selectProps.name ?? undefined;
  const selectComponentProps = {
    ...selectProps,
    id: resolvedId,
    name: selectProps.name ?? "parqueadero",
  };

  return (
    <FieldBasic
      label={resolvedLabel}
      htmlFor={resolvedId}
      required={required && Boolean(resolvedLabel)}
      errors={errors}
      helperText={helperText}
      layout={layout}
      className={fieldClassName}
      contentClassName={contentClassName}
    >
      <CustomSelectParqueaderos
        options={resolvedOptions}
        placeholder={placeholder}
        className={selectClassName}
        {...selectComponentProps}
      />
    </FieldBasic>
  );
};
